import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, Heart, HeartOff } from 'lucide-react';

const FlashCardWithAnimation = ({ 
  card, 
  showAnswer, 
  onShowAnswer, 
  onSpeak, 
  soundEnabled,
  onSave,
  isSaved = false
}) => {
  const [isFlipping, setIsFlipping] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const handleFlip = useCallback(() => {
    setIsFlipping(true);
    setTimeout(() => {
      setIsFlipped(prev => !prev);
      setIsFlipping(false);
    }, 300);
  }, []);

  useEffect(() => {
    if (showAnswer && !isFlipped) {
      handleFlip();
    } else if (!showAnswer && isFlipped) {
      handleFlip();
    }
  }, [showAnswer, isFlipped, handleFlip]);

  const handleCardClick = () => {
    if (!showAnswer) {
      onShowAnswer();
    }
  };

  const handleSaveCard = () => {
    if (onSave) {
      onSave(card);
    }
  };

  return (
    <div className="flash-card-container">
      <style jsx>{`
        .flash-card-container {
          perspective: 1000px;
          width: 100%;
          height: 500px;
          position: relative;
        }
        
        .flash-card {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);
          cursor: pointer;
        }
        
        .flash-card.flipped {
          transform: rotateY(180deg);
        }
        
        .flash-card.flipping {
          animation: whirl 0.6s ease-in-out;
        }
        
        @keyframes whirl {
          0% { transform: rotateY(0deg) scale(1); }
          25% { transform: rotateY(45deg) scale(1.05); }
          50% { transform: rotateY(90deg) scale(1.1); }
          75% { transform: rotateY(135deg) scale(1.05); }
          100% { transform: rotateY(180deg) scale(1); }
        }
        
        .card-face {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          background: white;
          border: 1px solid #e5e7eb;
        }
        
        .card-face.back {
          transform: rotateY(180deg);
        }
        
        .card-image {
          height: 200px;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          position: relative;
          overflow: hidden;
        }
        
        .card-image::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(147, 51, 234, 0.1), rgba(219, 39, 119, 0.1));
        }
        
        .card-content {
          padding: 24px;
          height: calc(100% - 200px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        
        .question-side {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .answer-side {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white;
        }
        
        .card-controls {
          position: absolute;
          top: 16px;
          right: 16px;
          display: flex;
          gap: 8px;
          z-index: 10;
        }
        
        .control-button {
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          backdrop-filter: blur(10px);
        }
        
        .control-button:hover {
          background: rgba(255, 255, 255, 1);
          transform: scale(1.1);
        }
        
        .control-button.active {
          background: rgba(147, 51, 234, 0.9);
          color: white;
        }
        
        .control-button.saved {
          background: rgba(239, 68, 68, 0.9);
          color: white;
        }
        
        .card-text {
          font-size: 1.25rem;
          line-height: 1.6;
          text-align: center;
          margin: auto 0;
        }
        
        .card-subtitle {
          font-size: 0.875rem;
          opacity: 0.8;
          text-align: center;
          margin-bottom: 16px;
        }
        
        .keywords-container {
          margin-top: 16px;
        }
        
        .keyword-tag {
          display: inline-block;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 0.75rem;
          margin: 2px;
          backdrop-filter: blur(10px);
        }
        
        .explanation-text {
          font-size: 0.95rem;
          line-height: 1.5;
          margin-top: 12px;
          opacity: 0.9;
        }
        
        .tap-hint {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          backdrop-filter: blur(10px);
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
      
      <div 
        className={`flash-card ${isFlipped ? 'flipped' : ''} ${isFlipping ? 'flipping' : ''}`}
        onClick={handleCardClick}
      >
        {/* Front Side - Question */}
        <div className="card-face front question-side">
          <div className="card-controls">
            {soundEnabled && (
              <button
                className="control-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSpeak(card.question);
                }}
                title="Play pronunciation"
              >
                <Volume2 size={16} />
              </button>
            )}
            <button
              className={`control-button ${isSaved ? 'saved' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveCard();
              }}
              title={isSaved ? "Remove from saved" : "Save flash card"}
            >
              {isSaved ? <Heart size={16} /> : <HeartOff size={16} />}
            </button>
          </div>
          
          <div 
            className="card-image"
            style={{ 
              backgroundImage: `url(${card.imageUrl})` 
            }}
          />
          
          <div className="card-content">
            <div className="card-subtitle">Question</div>
            <div className="card-text">{card.question}</div>
            {!showAnswer && (
              <div className="tap-hint">
                Tap to reveal answer
              </div>
            )}
          </div>
        </div>

        {/* Back Side - Answer */}
        <div className="card-face back answer-side">
          <div className="card-controls">
            {soundEnabled && (
              <button
                className="control-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSpeak(card.answer);
                }}
                title="Play pronunciation"
              >
                <Volume2 size={16} />
              </button>
            )}
            <button
              className={`control-button ${isSaved ? 'saved' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveCard();
              }}
              title={isSaved ? "Remove from saved" : "Save flash card"}
            >
              {isSaved ? <Heart size={16} /> : <HeartOff size={16} />}
            </button>
          </div>
          
          <div 
            className="card-image"
            style={{ 
              backgroundImage: `url(${card.imageUrl})` 
            }}
          />
          
          <div className="card-content">
            <div className="card-subtitle">Answer</div>
            <div className="card-text">{card.answer}</div>
            
            {card.explanation && (
              <div className="explanation-text">
                {card.explanation}
              </div>
            )}
            
            {card.keywords && card.keywords.length > 0 && (
              <div className="keywords-container">
                {card.keywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashCardWithAnimation; 